import React, { useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import './ChatApp.css'; // Add custom styles for the chat UI
import Picker from 'emoji-picker-react';

const RL_CORE_URL = 'https://rl-core-app-kpx3b.ondigitalocean.app';
const CHAT_BACKEND_URL = 'https://whale-app-6qyfq.ondigitalocean.app';

const ChatApp = ({ closeChat }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStudent, setIsStudent] = useState(false); // State to check if the user is a student
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isBanned, setIsBanned] = useState(false); // State to track if user is banned
  const [banTimeRemaining, setBanTimeRemaining] = useState(0); // Seconds remaining on temp ban
  const [isTempBan, setIsTempBan] = useState(false); // Is this a temporary ban?
  const chatWindowRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const socketRef = useRef(null); // Store socket reference
  const banTimerRef = useRef(null); // Timer reference for cleanup
  const banCheckIntervalRef = useRef(null); // Periodic ban check interval
  // Maintenance mode state - fetched from API
  const [maintenanceMode, setMaintenanceMode] = useState(null); // null = loading
  const [bypassUsers, setBypassUsers] = useState([]);

  // Block/Report feature state
  const [blockedUsers, setBlockedUsers] = useState([]); // Array of {userId, username}
  const [showBlockedUsersPanel, setShowBlockedUsersPanel] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // {userId, username, message}
  const [showUserMenu, setShowUserMenu] = useState(null); // userId of menu being shown
  const [reportReason, setReportReason] = useState('');
  const userMenuRef = useRef(null);

  // Fetch maintenance status from API on mount
  useEffect(() => {
    const fetchMaintenanceStatus = async () => {
      try {
        const response = await fetch(`${RL_CORE_URL}/chat/maintenance-status`);
        const data = await response.json();
        setMaintenanceMode(data.maintenance_mode);
        setBypassUsers(data.bypass_users || []);
      } catch (error) {
        console.error('Error fetching maintenance status:', error);
        // Default to no maintenance if API fails
        setMaintenanceMode(false);
        setBypassUsers([]);
      }
    };
    fetchMaintenanceStatus();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const userType = localStorage.getItem('usertype');
    const userId = localStorage.getItem('user_id');

    if (userType === 'student') {
      setIsStudent(true);
      setCurrentUserId(userId);
    } else {
      setIsStudent(false);
      setCurrentUserId(null);
      console.warn('Chat is only available for student accounts.');
      return; // Exit early if not a student
    }

    const socket = io('https://whale-app-6qyfq.ondigitalocean.app', {
      auth: {
        token: `Bearer ${token}`,
      },
    });

    // Store socket reference for use in sendMessage
    socketRef.current = socket;

    const fetchChatHistory = async () => {
      try {
        const response = await fetch('https://whale-app-6qyfq.ondigitalocean.app/get-recent-messages', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();

        const formattedMessages = data.map((msg) => ({
          ...msg,
          isSelf: String(msg.sender_id) === String(userId),
        }));

        setMessages(formattedMessages);
        setTimeout(scrollToBottom, 100); // Scroll to the bottom after loading history
      } catch (error) {
        console.error('Error loading chat history:', error);
      }
    };

    const checkBanStatus = async () => {
      try {
        const response = await fetch(`https://whale-app-6qyfq.ondigitalocean.app/admin/check-ban/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();

        if (data.isBanned) {
          setIsBanned(true);

          if (data.tempBan && data.secondsRemaining > 0) {
            // TEMP BAN - set up countdown
            setIsTempBan(true);
            setBanTimeRemaining(data.secondsRemaining);

            const banMessage = {
              username: 'SYSTEM',
              message: `🚫 You are in timeout for ${Math.ceil(data.secondsRemaining / 60)} minutes. Reason: ${data.banInfo.reason}`,
              isSelf: true,
              isSystem: true,
              isBan: true
            };
            setMessages((prevMessages) => [...prevMessages, banMessage]);

            // Start countdown timer
            banTimerRef.current = setInterval(() => {
              setBanTimeRemaining((prev) => {
                if (prev <= 1) {
                  clearInterval(banTimerRef.current);
                  setIsBanned(false);
                  setIsTempBan(false);

                  const unbanMessage = {
                    username: 'SYSTEM',
                    message: '✅ Your timeout has ended. Please follow the chat rules.',
                    isSelf: true,
                    isSystem: true
                  };
                  setMessages((prevMessages) => [...prevMessages, unbanMessage]);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          } else {
            // PERMANENT BAN
            setIsTempBan(false);
            const banMessage = {
              username: 'SYSTEM - IMPORTANT',
              message: `🚫 You are currently banned from chat. Reason: ${data.banInfo.reason}`,
              isSelf: true,
              isSystem: true,
              isBan: true
            };
            setMessages((prevMessages) => [...prevMessages, banMessage]);
          }
        }
      } catch (error) {
        console.error('Error checking ban status:', error);
      }
    };

    if (isStudent) {
      fetchChatHistory();
      checkBanStatus();

      // Periodic ban status check every 30 seconds to catch admin-initiated bans
      banCheckIntervalRef.current = setInterval(() => {
        checkBanStatus();
      }, 30000);
    }

    socket.on('receive-message', (msg) => {
      setMessages((prevMessages) => {
        // Check if we are scrolled to the bottom
        const shouldScroll =
          chatWindowRef.current.scrollHeight - chatWindowRef.current.scrollTop ===
          chatWindowRef.current.clientHeight;

        const newMessages = [
          ...prevMessages,
          { ...msg, isSelf: String(msg.sender_id) === String(userId) },
        ];

        // Auto-scroll if we were already at the bottom
        if (shouldScroll) {
          setTimeout(scrollToBottom, 100);
        }

        return newMessages;
      });
    });

    socket.on('message-flagged', (data) => {
      // Check if this is a safeguarding/crisis message (should persist, not disappear)
      const isCrisisMessage = data.message && (
        data.message.includes('Childline') ||
        data.message.includes('Samaritans') ||
        data.message.includes('we care about you') ||
        data.message.includes('SHOUT')
      );

      const systemMessage = {
        username: isCrisisMessage ? 'Support' : 'System',
        message: data.message,
        isSelf: true,
        isSystem: true,
        isCrisis: isCrisisMessage,  // Flag for styling
      };

      setMessages((prevMessages) => [...prevMessages, systemMessage]);

      // Only auto-remove non-crisis messages (warnings etc)
      if (!isCrisisMessage) {
        setTimeout(() => {
          setMessages((prevMessages) => prevMessages.filter((msg) => msg !== systemMessage));
        }, 5000);
      }
    });

    // Handle error messages from server
    socket.on('error-message', (data) => {
      console.error('Socket error:', data);
      const errorMessage = {
        username: 'System',
        message: typeof data === 'string' ? data : (data.message || 'An error occurred. Please refresh the page.'),
        isSelf: true,
        isSystem: true,
        isError: true,
      };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    });

    socket.on('user-banned', (data) => {
      // Clear any existing timer
      if (banTimerRef.current) {
        clearInterval(banTimerRef.current);
      }

      // Set banned state
      setIsBanned(true);
      setInput('');
      setShowEmojiPicker(false);

      if (data.tempBan && data.secondsRemaining > 0) {
        // TEMPORARY BAN - show countdown
        setIsTempBan(true);
        setBanTimeRemaining(data.secondsRemaining);

        // Show temp ban message
        const banMessage = {
          username: 'SYSTEM',
          message: `🚫 ${data.message}`,
          isSelf: true,
          isSystem: true,
          isBan: true
        };
        setMessages((prevMessages) => [...prevMessages, banMessage]);

        // Start countdown timer
        banTimerRef.current = setInterval(() => {
          setBanTimeRemaining((prev) => {
            if (prev <= 1) {
              // Ban expired - restore access
              clearInterval(banTimerRef.current);
              setIsBanned(false);
              setIsTempBan(false);

              // Show unban message
              const unbanMessage = {
                username: 'SYSTEM',
                message: '✅ Your timeout has ended. Please follow the chat rules.',
                isSelf: true,
                isSystem: true
              };
              setMessages((prevMessages) => [...prevMessages, unbanMessage]);

              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        // PERMANENT BAN
        setIsTempBan(false);
        setBanTimeRemaining(0);

        const banMessage = {
          username: 'SYSTEM - IMPORTANT',
          message: `🚫 BANNED: ${data.message}`,
          isSelf: true,
          isSystem: true,
          isBan: true
        };
        setMessages((prevMessages) => [...prevMessages, banMessage]);

        // Show alert for permanent bans only
        alert(`🚫 CHAT BANNED\n\n${data.message}\n\nYour chat access has been suspended. Contact support if you believe this is an error.`);
      }
    });

    return () => {
      socket.disconnect(); // Cleanup socket connection
      if (banTimerRef.current) {
        clearInterval(banTimerRef.current); // Cleanup ban timer
      }
      if (banCheckIntervalRef.current) {
        clearInterval(banCheckIntervalRef.current); // Cleanup periodic ban check
      }
    };
  }, [isStudent]);

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  };

  const sendMessage = () => {
    if (input.trim() && isStudent && !isBanned && socketRef.current) {
      const currentUsername = localStorage.getItem('username');

      const msg = {
        message: input,
        userId: currentUserId,
        username: currentUsername,
        sender_id: currentUserId,
      };

      // Use the existing socket connection
      socketRef.current.emit('send-message', msg);
      setInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleEmojiClick = (emojiData, event) => {
    // `emojiData.emoji` will be the actual emoji character
    setInput((prevInput) => prevInput + emojiData.emoji);
    setShowEmojiPicker(false); // close the picker after selection
  };

  // Fetch blocked users list
  const fetchBlockedUsers = async () => {
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch(`${CHAT_BACKEND_URL}/api/blocked-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBlockedUsers(data.blockedUsers);
      }
    } catch (error) {
      console.error('Error fetching blocked users:', error);
    }
  };

  // Fetch blocked users on mount
  useEffect(() => {
    if (isStudent && currentUserId) {
      fetchBlockedUsers();
    }
  }, [isStudent, currentUserId]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Block user handler
  const handleBlockUser = async () => {
    if (!selectedUser) return;
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch(`${CHAT_BACKEND_URL}/api/block-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ blockedUserId: selectedUser.userId })
      });
      const data = await response.json();
      if (data.success) {
        // Add to local blocked list
        setBlockedUsers(prev => [...prev, { id: selectedUser.userId, username: selectedUser.username }]);
        // Filter out messages from blocked user
        setMessages(prev => prev.filter(msg => String(msg.sender_id) !== String(selectedUser.userId)));
        setShowBlockModal(false);
        setSelectedUser(null);
      } else {
        alert(data.message || 'Could not block user');
      }
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Could not block user. Please try again.');
    }
  };

  // Unblock user handler
  const handleUnblockUser = async () => {
    if (!selectedUser) return;
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch(`${CHAT_BACKEND_URL}/api/unblock-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ blockedUserId: selectedUser.userId })
      });
      const data = await response.json();
      if (data.success) {
        // Remove from local blocked list
        setBlockedUsers(prev => prev.filter(u => u.id !== selectedUser.userId));
        setShowUnblockModal(false);
        setSelectedUser(null);
      } else {
        alert(data.message || 'Could not unblock user');
      }
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert('Could not unblock user. Please try again.');
    }
  };

  // Report user handler
  const handleReportUser = async () => {
    if (!selectedUser || !reportReason) return;
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch(`${CHAT_BACKEND_URL}/api/report-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          reportedUserId: selectedUser.userId,
          reportedMessage: selectedUser.message || '',
          reason: reportReason
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowReportModal(false);
        setSelectedUser(null);
        setReportReason('');
        // Show confirmation
        const confirmMessage = {
          username: 'System',
          message: 'Report submitted. Thanks for helping keep chat safe.',
          isSelf: true,
          isSystem: true
        };
        setMessages(prev => [...prev, confirmMessage]);
        setTimeout(() => {
          setMessages(prev => prev.filter(msg => msg !== confirmMessage));
        }, 5000);
      } else {
        alert(data.message || 'Could not submit report');
      }
    } catch (error) {
      console.error('Error reporting user:', error);
      alert('Could not submit report. Please try again.');
    }
  };

  // Open user action menu
  const openUserMenu = (userId, username, message, event, messageIndex) => {
    event.stopPropagation();
    // Don't allow blocking/reporting yourself
    if (String(userId) === String(currentUserId)) return;
    setSelectedUser({ userId, username, message });
    setShowUserMenu(messageIndex);
  };

  useEffect(() => {
    if (!isStudent) return; // Only run if the user is a student

    const chatContainer = chatContainerRef.current;
    const resizer = document.querySelector(".chat-resizer");
    if (!chatContainer || !resizer) return;
  
    let isResizing = false;
    let startX, startY, startWidth, startHeight, startLeft, startTop;
  
    const handleMouseDown = (e) => {
      isResizing = true;
      startX = e.clientX;
      startY = e.clientY;
      startWidth = chatContainer.offsetWidth;
      startHeight = chatContainer.offsetHeight;
      startLeft = chatContainer.offsetLeft;
      startTop = chatContainer.offsetTop;
  
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };
  
    const handleMouseMove = (e) => {
      if (!isResizing) return;
  
      const diffX = e.clientX - startX;
      const diffY = e.clientY - startY;
  
      let newWidth = startWidth - diffX;
      let newHeight = startHeight - diffY;
  
      // Limit sizes
      newWidth = Math.max(250, Math.min(newWidth, window.innerWidth * 0.9));
      newHeight = Math.max(300, Math.min(newHeight, window.innerHeight * 0.9));
  
      chatContainer.style.width = `${newWidth}px`;
      chatContainer.style.height = `${newHeight}px`;
  
      // Update position so resizing happens from top-left
      chatContainer.style.left = `${startLeft + diffX}px`;
      chatContainer.style.top = `${startTop + diffY}px`;
    };
  
    const handleMouseUp = () => {
      isResizing = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  
    resizer.addEventListener("mousedown", handleMouseDown);
  
    return () => {
      resizer.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isStudent]);
  
  
  


  // Loading state while fetching maintenance status
  if (maintenanceMode === null) {
    return (
      <div className="chat-container" style={{ width: '350px', height: '400px' }}>
        <div className="chat-header">
          <button onClick={closeChat} className="chat-close-button" aria-label="Close chat">
            ✖
          </button>
          <img
            src="https://websitecontent.ams3.cdn.digitaloceanspaces.com/RocketChatV3.png"
            alt="RocketChat"
            className="chat-header-logo"
          />
        </div>
        <div className="chat-window" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Maintenance mode - show friendly reminder (unless user is in bypass list)
  if (maintenanceMode && !bypassUsers.includes(String(currentUserId))) {
    return (
      <div className="chat-container" style={{ width: '350px', height: '400px' }}>
        <div className="chat-header">
          <button onClick={closeChat} className="chat-close-button" aria-label="Close chat">
            ✖
          </button>
          <img
            src="https://websitecontent.ams3.cdn.digitaloceanspaces.com/RocketChatV3.png"
            alt="RocketChat"
            className="chat-header-logo"
          />
        </div>
        <div className="chat-window" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>🚀🔧</div>
          <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Chat is Taking a Quick Break!</h3>
          <p style={{ margin: '0 0 15px 0', color: '#666', lineHeight: '1.5' }}>
            We're making some improvements to keep our community safe and friendly.
          </p>
          <p style={{ margin: '0', color: '#888', fontSize: '14px', fontStyle: 'italic' }}>
             Thanks for being patient — chat will be back soon. 💙
          </p>
        </div>
      </div>
    );
  }

  if (!isStudent) {
    return (
      <div className="chat-container">
        <div className="chat-header">
        <button onClick={closeChat} className="chat-close-button" aria-label="Close chat">
  ✖
</button>
          <h4>Chat Unavailable</h4>
        </div>
        <div className="chat-window">
          <p style={{ textAlign: 'center', marginTop: '20px' }}>
            Chat is only available for student accounts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container" ref={chatContainerRef}>
      {/* Header */}
      <div className="chat-header">
        <div className="chat-resizer">
          <div className="resize-handle" />
        </div>
        <button
          onClick={() => setShowBlockedUsersPanel(true)}
          className="chat-settings-button"
          aria-label="Settings"
          title="Blocked users"
        >
          ⚙️
        </button>
        <button onClick={closeChat} className="chat-close-button" aria-label="Close chat">
          ✖
        </button>
        <img
          src="https://websitecontent.ams3.cdn.digitaloceanspaces.com/RocketChatV3.png"
          alt="RocketChat v1"
          className="chat-header-logo"
        />
      </div>
  
      {/* Chat Window */}
      <div className="chat-window" ref={chatWindowRef}>
      {messages.map((msg, index) => {
  // If it's a bot or system message (sender_id=0 or isSystem flag):
  if (String(msg.sender_id) === '0' || msg.isSystem) {
    // Crisis messages get special styling - they persist and are important
    const isCrisis = msg.isCrisis || msg.message?.includes('Childline');
    const isError = msg.isError;
    return (
      <div
        key={index}
        className={`chat-message chat-message-system ${isCrisis ? 'chat-message-crisis' : ''} ${isError ? 'chat-message-error' : ''}`}
        style={isCrisis ? {
          backgroundColor: '#e0f2fe',
          border: '2px solid #0ea5e9',
          borderRadius: '12px',
          padding: '12px',
          margin: '8px 0',
          whiteSpace: 'pre-line'
        } : isError ? {
          backgroundColor: '#fef2f2',
          border: '2px solid #ef4444',
          borderRadius: '12px',
          padding: '12px',
          margin: '8px 0'
        } : undefined}
      >
        <strong>{msg.username}:</strong> {msg.message}
      </div>
    );
  }
  // else normal user message:
  const isOtherUser = !msg.isSelf && String(msg.sender_id) !== String(currentUserId);
  return (
    <div
      key={index}
      className={`chat-message ${
        msg.isSelf ? 'chat-message-self' : 'chat-message-other'
      }`}
    >
      {isOtherUser ? (
        <span className="chat-username-container">
          <strong
            className="chat-clickable-username"
            onClick={(e) => openUserMenu(msg.sender_id, msg.username, msg.message, e, index)}
            title="Click for options"
          >
            {msg.username}:
          </strong>
          {showUserMenu === index && (
            <div className="chat-user-menu" ref={userMenuRef}>
              <button
                className="chat-user-menu-item"
                onClick={() => { setShowUserMenu(null); setShowBlockModal(true); }}
              >
                🚫 Block {msg.username}
              </button>
              <button
                className="chat-user-menu-item"
                onClick={() => { setShowUserMenu(null); setShowReportModal(true); }}
              >
                🚩 Report this message
              </button>
            </div>
          )}
        </span>
      ) : (
        <strong>{msg.username}:</strong>
      )}
      {' '}{msg.message}
    </div>
  );
})}
      </div>
  
      {/* Input Container */}
      <div className="chat-input-container">
        {/*
          Input field
        */}
        <input
          type="text"
          value={input}
          onChange={(e) => !isBanned && setInput(e.target.value)}
          placeholder={
            isBanned
              ? isTempBan
                ? `⏱️ Timeout: ${formatTime(banTimeRemaining)} remaining`
                : "Chat disabled - You are banned"
              : "Type your message..."
          }
          className={`chat-input ${isBanned ? 'chat-input-disabled' : ''}`}
          onKeyPress={handleKeyPress}
          disabled={isBanned}
        />
  
        {/*
          Emoji button
        */}
        <button
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className="chat-emoji-button"
        >
          😀
        </button>
  
        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div className="emoji-picker-container">
            <Picker onEmojiClick={handleEmojiClick} />
          </div>
        )}
  
        {/*
          Send button
        */}
        <button
          onClick={sendMessage}
          className={`chat-send-button ${isBanned ? 'chat-send-button-disabled' : ''}`}
          disabled={isBanned}
        >
          {isBanned ? (isTempBan ? formatTime(banTimeRemaining) : 'Banned') : 'Send'}
        </button>
      </div>

      {/* Blocked Users Panel */}
      {showBlockedUsersPanel && (
        <div className="chat-modal-overlay" onClick={() => setShowBlockedUsersPanel(false)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-modal-header">
              <h3>Blocked Users</h3>
              <button
                className="chat-modal-close"
                onClick={() => setShowBlockedUsersPanel(false)}
              >
                ✖
              </button>
            </div>
            <div className="chat-modal-body">
              {blockedUsers.length === 0 ? (
                <p className="chat-modal-empty">You haven't blocked anyone</p>
              ) : (
                <ul className="chat-blocked-list">
                  {blockedUsers.map((user) => (
                    <li key={user.id} className="chat-blocked-item">
                      <span>{user.username}</span>
                      <button
                        className="chat-unblock-button"
                        onClick={() => {
                          setSelectedUser({ userId: user.id, username: user.username });
                          setShowUnblockModal(true);
                        }}
                      >
                        Unblock
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Block Confirmation Modal */}
      {showBlockModal && selectedUser && (
        <div className="chat-modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-modal-header">
              <h3>Block {selectedUser.username}?</h3>
              <button
                className="chat-modal-close"
                onClick={() => setShowBlockModal(false)}
              >
                ✖
              </button>
            </div>
            <div className="chat-modal-body">
              <p>You won't see their messages and they won't see yours.</p>
              <div className="chat-modal-actions">
                <button
                  className="chat-modal-button chat-modal-button-cancel"
                  onClick={() => setShowBlockModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="chat-modal-button chat-modal-button-confirm"
                  onClick={handleBlockUser}
                >
                  Block
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unblock Confirmation Modal */}
      {showUnblockModal && selectedUser && (
        <div className="chat-modal-overlay" onClick={() => setShowUnblockModal(false)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-modal-header">
              <h3>Unblock {selectedUser.username}?</h3>
              <button
                className="chat-modal-close"
                onClick={() => setShowUnblockModal(false)}
              >
                ✖
              </button>
            </div>
            <div className="chat-modal-body">
              <p>You'll be able to see their messages again.</p>
              <div className="chat-modal-actions">
                <button
                  className="chat-modal-button chat-modal-button-cancel"
                  onClick={() => setShowUnblockModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="chat-modal-button chat-modal-button-confirm"
                  onClick={handleUnblockUser}
                >
                  Unblock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && selectedUser && (
        <div className="chat-modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-modal-header">
              <h3>Report {selectedUser.username}</h3>
              <button
                className="chat-modal-close"
                onClick={() => { setShowReportModal(false); setReportReason(''); }}
              >
                ✖
              </button>
            </div>
            <div className="chat-modal-body">
              <p>Why are you reporting this message?</p>
              <select
                className="chat-report-select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">Select a reason...</option>
                <option value="Being mean or unkind">Being mean or unkind</option>
                <option value="Using bad language">Using bad language</option>
                <option value="Making me uncomfortable">Making me uncomfortable</option>
                <option value="Asking for personal information">Asking for personal information</option>
                <option value="Other">Other</option>
              </select>
              <div className="chat-modal-actions">
                <button
                  className="chat-modal-button chat-modal-button-cancel"
                  onClick={() => { setShowReportModal(false); setReportReason(''); }}
                >
                  Cancel
                </button>
                <button
                  className="chat-modal-button chat-modal-button-confirm"
                  onClick={handleReportUser}
                  disabled={!reportReason}
                >
                  Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatApp;

